<template>
  <base-page-layout ref="base-page" :title="alteracaoTitle" :hide-breadcrumbs="$getFirstValue()" class="alteracao-vouchers">
    <Header :campaign="voucher" :validations="$v" />
    <segment-toggle :campaign="voucher" :validations="$v" @update:image="updateVoucher" @register-validation="registerImageValidation" />
    <action-buttons :save="save" :object="voucher" />
  </base-page-layout>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import ActionButtons from '../components/edit/ActionButtons.vue'
import SegmentToggle from '../components/edit/SegmentToggle.vue'
import Header from '../components/edit/Header.vue'

import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { notOnlySpace, initialDateNotPast } from '@/helpers/validators.js'
import { deepDelete } from '@/helpers/deepDelete'
import { VMoney } from 'v-money'
import { MUTATION_UPSERT_POINTS_VOUCHER } from '../../graphql'
import Swal from 'sweetalert2'
import cloneDeep from 'lodash/cloneDeep'
import { mapGetters } from 'vuex'

export default {
  components: {
    BasePageLayout,
    ActionButtons,
    SegmentToggle,
    Header,
  },
  directives: { money: VMoney },
  props: {
    voucher: {
      type: Object,
      required: true,
      default: () => ({
        _id: null,
        title: '',
        type: 'ABSOLUTE_DISCOUNT',
        description: '',
        money: '',
        image: '',
        product_description: '',
        points: 0,
        expires_in_days: 30,
        initial_date: '',
        final_date: '',
        draft: false,
        rescue_limit: null,
        user_limit: null,
        units: [],
      }),
    },
  },
  data: () => ({
    vPoints: { thousands: '.', precision: 0 },
    savingDraft: false,
    moneyLocal: '',
    pointsLocal: '',
    imageValidationFn: null,
  }),
  computed: {
    ...mapGetters(['units', 'tagList', 'unitsGroups']),
    money: {
      get() {
        return this.moneyLocal
      },
      set(value) {
        let v = value.split('.').join('').split(',').join('.')
        this.voucher.money = Number(v.replace(/[^0-9.]/g, '')) > 0 ? Number(v.replace(/[^0-9.]/g, '')) : ''
        this.moneyLocal = value
      },
    },
    points: {
      get() {
        return this.pointsLocal
      },
      set(value) {
        let p = value.split('.').join('')
        this.voucher.points = Number(p.replace(/[^0-9.]/g, '')) > 0 ? Number(p.replace(/[^0-9.]/g, '')) : ''
        this.pointsLocal = value
      },
    },
    isActive() {
      return Boolean(this.voucher.status === 'ACTIVE' || this.voucher.status === 'FINISHED')
    },
    newVoucher() {
      return this.$router.currentRoute.fullPath.includes('novo')
    },
    alteracaoTitle() {
      return this.newVoucher ? 'Novo vale-troca' : 'Editar vale-troca'
    },
    errorSendDate() {
      if (!this.$v.voucher?.initial_date) return ''
      if (this.$v.voucher.initial_date.$error) {
        if (!this.$v.voucher.initial_date.required) return 'Selecione uma data de envio.'
      }
      return ''
    },
    errorFinalDate() {
      if (!this.$v.voucher?.final_date) return ''
      if (this.$v.voucher.final_date.$error) {
        if (!this.$v.voucher.final_date.required) return 'Selecione uma data de fim.'
      }
      return ''
    },
  },
  created() {
    if (!this.newVoucher) {
      this.pointsLocal = this.voucher.points
      this.moneyLocal = this.voucher.money
    }
  },
  methods: {
    updateVoucher(updatedVoucher) {
      this.voucher.image = updatedVoucher.image
    },
    registerImageValidation(validationFn) {
      this.imageValidationFn = validationFn
    },
    async save(isDraft) {
      this.$v.$reset()

      if (isDraft) {
        this.$v.voucher.title.$touch()
        if (this.$v.voucher.title.$error) {
          this.$snackbar({ message: 'Preencha o título corretamente.', snackbarColor: 'error' })
          return
        }
      } else {
        this.$v.$touch()

        const imageValidationPassed = !this.imageValidationFn || this.imageValidationFn()

        if (this.$v.$error || !imageValidationPassed) {
          this.$snackbar({ message: 'Preencha todos os campos corretamente.', snackbarColor: 'error' })
          this.$nextTick(() => {
            this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
          })
          return
        }
      }

      const voucher = cloneDeep(this.voucher)
      deepDelete(voucher, ['__typename', 'status', 'updated_at', 'remaining_rescue_limit'])
      voucher.draft = !!isDraft

      if (voucher.image && voucher.image.includes('...')) {
        voucher.image = voucher.image.replace('...', '')
      }

      if (voucher.money !== null && voucher.money !== undefined && voucher.money !== '') {
        voucher.money = parseFloat(voucher.money)
      }

      if (voucher.type === 'PRODUCT') {
        delete voucher.money
      } else if (voucher.type === 'ABSOLUTE_DISCOUNT') {
        delete voucher.product_description
      }

      const input = isDraft ? this.cleanDraftPayload(voucher) : voucher

      try {
        await this.$apollo.mutate({
          mutation: MUTATION_UPSERT_POINTS_VOUCHER,
          variables: { input },
        })

        Swal.fire({
          text: isDraft ? 'Rascunho salvo com sucesso!' : 'Vale-troca salvo com sucesso!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1000,
          customClass: { content: 'content-class' },
        })

        this.$router.push('/mobile/pontos').catch(() => {})
      } catch (e) {
        this.$snackbar({ message: 'Erro ao salvar o vale-troca.', snackbarColor: 'error' })
      }
    },
    cleanDraftPayload(payload) {
      if (payload.type === 'PRODUCT') {
        payload.money = null
      } else if (payload.type === 'ABSOLUTE_DISCOUNT') {
        payload.product_description = null
      }

      const result = {}

      for (const key in payload) {
        const value = payload[key]
        const isEmptyString = value === ''
        const isEmptyArray = Array.isArray(value) && value.length === 0
        const isNullOrUndefined = value === null || value === undefined

        if (isEmptyString || isEmptyArray || isNullOrUndefined) {
          result[key] = null
        } else {
          if (key === 'money') {
            result[key] = parseFloat(value)
          } else {
            result[key] = value
          }
        }
      }
      return result
    },
  },
  validations() {
    const validationRules = {
      voucher: {
        title: {
          required,
          minLength: minLength(2),
          maxLength: maxLength(70),
          notOnlySpace,
        },
        description: {
          required,
          minLength: minLength(2),
          maxLength: maxLength(150),
          notOnlySpace,
        },
        ...(this.voucher.type === 'PRODUCT'
          ? {
              product_description: {
                required,
                minLength: minLength(2),
                maxLength: maxLength(70),
                notOnlySpace,
              },
            }
          : {
              money: { required },
            }),
        type: { required },
        image: {},
        rescue_limit: {},
        user_limit: {},
        initial_date: {
          required,
          ...(this.isActive ? {} : { initialDateNotPast }),
        },
        final_date: { required },
        units: { required },
        points: { required },
      },
    }

    return validationRules
  },
}
</script>

<style lang="scss">
$phone-size: 600px;
$tablet-size: 960px;
.alteracao-vouchers {
  .voucher-date-fields {
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-top: 16px;

    & > * {
      flex: 1;
    }
  }
  min-width: 1020px;
  h3 {
    margin-bottom: 8px;
    font-size: 18px;
    font-weight: bold;
    letter-spacing: 0.33px;
    color: $cinza-d20;
  }
  h4 {
    font-size: 20px;
    font-weight: normal;
  }
  h5 {
    font-size: 16px;
    font-weight: normal;
  }
  .legenda {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: 0.233333px;
  }
  .hint-text {
    font-size: 14px;
    line-height: 21px;
    &.external {
      max-width: 700px;
    }
    b {
      color: $marca-1;
      font-weight: 700;
    }
  }
  .message-box {
    border: 1px solid $cinza-l90;
    border-radius: 5px;
  }
  .error-text {
    color: $vermelho-0 !important;

    font-size: 12px !important;
    padding-left: 0 !important;

    margin: 0;
  }
  .v-dialog__container {
    display: inline-block !important;
  }
  .voucher-layout-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    gap: 16px;
  }

  .voucher-units-section {
    flex: 0 0 30%;
    display: flex;
    flex-direction: column;
    margin-top: 24px;
    margin-left: 20px;
  }

  .voucher-units-title {
    margin-bottom: 26px;
    font-size: 20px;
    font-weight: 700;
    color: #4b525a;
  }

  .voucher-units-subtitle {
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 400;
    color: #656565;
  }

  .voucher-dates-section {
    flex: 0 0 70%;
    display: flex;
    flex-direction: column;
    margin-top: 24px;
    margin-left: 20px;
    display: flex;
    flex-direction: column;
  }
  .outer-frame-push {
    border: 2px solid $cinza-l70;
    border-radius: 40px 40px 0 0;
    border-bottom: 0;
    width: 430px;
    @media screen and (max-width: $tablet-size) {
      margin: 20px 30px 0;
      padding: 20px 10px 0;
    }
    @media screen and (min-width: $tablet-size) {
      margin: 20px 30px 0;
      padding: 20px 10px 0;
    }
    .camera-slot {
      border: 2px solid $cinza-l70;
      border-radius: 20px;
      display: inline-block;
      margin-bottom: 5vh;
      height: 20px;
      width: 20px;
      margin-right: 0.8vw;
    }
    .ear-speaker-slot {
      border: 2px solid $cinza-l70;
      border-radius: 30px;
      display: inline-block;
      margin-bottom: 50px;
      height: 20px;
      width: 100px;
      margin-left: 8px;
    }
    .inner-frame-mobile {
      border: 2px solid $cinza-l70;
      border-bottom: 0;
      height: 100%;
      overflow: hidden;
      .navbar {
        display: flex;
        flex-direction: row-reverse;
        background-color: $cinza-l90;
        padding: 15px;
        .navbar-padding {
          display: inline-block;
        }
      }
      .box-offers-placeholder {
        border-radius: 0 0 10px 10px;
        border: 1.5px solid $cinza-l70;
        height: 10px;
        margin: 0 15px 0 15px;
        border-top: 0;
      }
      .box-banner {
        height: 140px;
        margin: 20px;
        justify-content: center;
        align-items: center;
        display: flex;
      }
      .empty-banner {
        border: 1.5px solid $cinza-l70;
      }
      .box-buttons-group {
        flex-direction: row;
        display: flex;
        .box-button-placeholder {
          border-radius: 10px 10px 0 0;
          border: 1.5px solid $cinza-l70;
          border-bottom: 0;
          height: 300px;
          width: 300px;
          margin: 0 20px;
        }
        .first-button {
          margin-right: 0;
        }
      }
    }
  }
}
</style>
